/**
 * Filtered Content Functionality
 *
 * Handles filtering of content blocks based on navigation selection.
 * Works with Filter Navigation block + Filtered Content blocks matched by ID.
 */

/**
 * Initialize all filtered content on the page
 */
export function initFilteredContent() {
	const filterNavigations = document.querySelectorAll( '[data-filter-navigation]' );

	if ( ! filterNavigations.length ) {
		return;
	}

	filterNavigations.forEach( ( filterNav ) => {
		// Skip if already initialized
		if ( filterNav.dataset.filterInitialized === 'true' ) {
			return;
		}

		// Find all filtered content blocks that follow this navigation
		const { contentBlocks, container } = findFilteredContentBlocks( filterNav );
		const filterLinks = filterNav.querySelectorAll( '.navigation-links__link' );

		if ( ! filterLinks.length || ! contentBlocks.length ) {
			return;
		}

		// Store references on the element for popstate handler
		filterNav._filterData = { contentBlocks, container, filterLinks };

		// Initialize first filter or filter from URL hash
		initializeActiveFilter( filterNav, filterLinks, contentBlocks, container );

		// Add click handlers to all filter links
		filterLinks.forEach( ( link ) => {
			link.addEventListener( 'click', function( e ) {
				e.preventDefault();
				const filterId = this.getAttribute( 'data-filter-id' );
				if ( filterId ) {
					switchFilter( filterNav, filterLinks, contentBlocks, container, filterId, true );
				}
			} );
		} );

		// Mark as initialized
		filterNav.dataset.filterInitialized = 'true';
	} );

	// Add popstate handler for browser back/forward buttons
	if ( ! window._filterPopstateHandlerAdded ) {
		window.addEventListener( 'popstate', handleFilterPopState );
		window._filterPopstateHandlerAdded = true;
	}
}

/**
 * Handle browser back/forward buttons
 */
function handleFilterPopState() {
	const hash = window.location.hash.substring( 1 ); // Remove the #
	const filterNavigations = document.querySelectorAll( '[data-filter-navigation]' );

	filterNavigations.forEach( ( filterNav ) => {
		// Get filter links for this navigation
		const filterLinks = filterNav.querySelectorAll( '.navigation-links__link' );

		if ( ! filterLinks.length ) {
			return;
		}

		let targetFilterId;

		if ( hash ) {
			// If there's a hash, try to activate that filter
			const targetLink = filterNav.querySelector( `[data-filter-id="${ hash }"]` );

			if ( targetLink ) {
				targetFilterId = hash;
			} else {
				// Hash doesn't match any filter, default to "all" (first filter)
				const firstLink = filterLinks[ 0 ];
				targetFilterId = firstLink ? firstLink.getAttribute( 'data-filter-id' ) : 'all';
			}
		} else {
			// No hash - activate "all" (first filter)
			const firstLink = filterLinks[ 0 ];
			targetFilterId = firstLink ? firstLink.getAttribute( 'data-filter-id' ) : 'all';
		}

		// Always switch to ensure clean state
		// Note: contentBlocks and container are now unused but kept for compatibility
		if ( targetFilterId ) {
			const allContentBlocks = findAllFilteredContentBlocks();
			switchFilter( filterNav, filterLinks, allContentBlocks, null, targetFilterId, false );
		}
	} );
}

/**
 * Find all filtered content blocks on the entire page
 * Searches the whole document, not just siblings of the navigation
 *
 * @return {Array} Array of all filtered content block elements on the page
 */
function findAllFilteredContentBlocks() {
	const contentBlocks = [];

	// Find all filtered content blocks on the entire page
	const allBlocks = document.querySelectorAll( '.filtered-content-block' );

	allBlocks.forEach( ( block ) => {
		if ( ! contentBlocks.includes( block ) ) {
			contentBlocks.push( block );
		}
	} );

	return contentBlocks;
}

/**
 * Find all filtered content blocks associated with this filter navigation
 * Now returns all filtered content blocks on the page for global filtering
 *
 * @param {HTMLElement} filterNav - The filter navigation element (unused but kept for compatibility)
 * @return {Object} Object with contentBlocks array and container element
 */
function findFilteredContentBlocks( filterNav ) {
	// filterNav is kept for backward compatibility but not used
	// eslint-disable-next-line no-unused-vars
	void filterNav;
	// Get all filtered content blocks on the entire page
	const contentBlocks = findAllFilteredContentBlocks();

	// Container is no longer needed since we're not wrapping blocks
	// But we keep it for backward compatibility with existing code
	const container = null;

	return { contentBlocks, container };
}

/**
 * Initialize the active filter on page load
 *
 * @param {HTMLElement} filterNav     - The filter navigation element
 * @param {NodeList}    filterLinks   - All filter link elements
 * @param {Array}       contentBlocks - All filtered content blocks
 * @param {HTMLElement} container     - The container element
 */
function initializeActiveFilter( filterNav, filterLinks, contentBlocks, container ) {
	const hash = window.location.hash.substring( 1 ); // Remove the #

	// Check if URL has a hash and if it matches any filter ID
	if ( hash ) {
		const targetLink = filterNav.querySelector( `[data-filter-id="${ hash }"]` );

		if ( targetLink ) {
			switchFilter( filterNav, filterLinks, contentBlocks, container, hash, false );
			return;
		}
	}

	// If no hash or hash doesn't match, activate "all" (first filter)
	if ( filterLinks.length > 0 ) {
		const firstFilterId = filterLinks[ 0 ].getAttribute( 'data-filter-id' );
		// First filter should always be "all"
		if ( firstFilterId ) {
			switchFilter( filterNav, filterLinks, contentBlocks, container, firstFilterId, false );
		}
	}
}

/**
 * Switch to a specific filter
 *
 * @param {HTMLElement} filterNav      - The filter navigation element
 * @param {NodeList}    filterLinks    - All filter link elements
 * @param {Array}       contentBlocks  - All filtered content blocks (unused, we search globally now)
 * @param {HTMLElement} container      - The container element for transitions (unused but kept for compatibility)
 * @param {string}      targetFilterId - The ID of the filter to switch to
 * @param {boolean}     updateHistory  - Whether to update browser history
 */
function switchFilter( filterNav, filterLinks, contentBlocks, container, targetFilterId, updateHistory ) {
	// Update link states for this navigation
	filterLinks.forEach( ( link ) => {
		const filterId = link.getAttribute( 'data-filter-id' );
		const isActive = filterId === targetFilterId;

		// Update active state classes
		if ( isActive ) {
			link.classList.add( 'text-orange' );
			link.classList.remove( 'text-black' );
			link.setAttribute( 'aria-current', 'page' );
		} else {
			link.classList.remove( 'text-orange' );
			link.classList.add( 'text-black' );
			link.removeAttribute( 'aria-current' );
		}

		// Update active indicator visibility
		const indicator = link.querySelector( '.navigation-links__active-indicator' );
		if ( indicator ) {
			if ( isActive ) {
				indicator.classList.remove( 'hidden' );
			} else {
				indicator.classList.add( 'hidden' );
			}
		}
	} );

	// Get ALL filtered content blocks on the entire page
	const allContentBlocks = findAllFilteredContentBlocks();

	// Update content block visibility for ALL blocks on the page
	// Special case: "all" shows all content blocks
	const showAll = targetFilterId === 'all';

	allContentBlocks.forEach( ( contentBlock ) => {
		const filterId = contentBlock.getAttribute( 'data-filter-id' );
		const isActive = showAll || filterId === targetFilterId;

		if ( isActive ) {
			contentBlock.style.display = '';
			contentBlock.setAttribute( 'aria-hidden', 'false' );
			// Trigger any lazy loading or initialization
			triggerContentActivation( contentBlock );
		} else {
			contentBlock.style.display = 'none';
			contentBlock.setAttribute( 'aria-hidden', 'true' );
		}
	} );

	// Update URL hash if updateHistory is true
	if ( updateHistory ) {
		const newUrl = window.location.pathname + window.location.search + '#' + targetFilterId;
		window.history.pushState( { filterId: targetFilterId }, '', newUrl );
	}

	/**
	 * Fire custom event for when a filter is activated
	 * Other scripts can listen to this event
	 */
	const event = new CustomEvent( 'filterActivated', {
		detail: {
			filterId: targetFilterId,
			navigation: filterNav,
			contents: allContentBlocks,
			container,
		},
	} );
	document.dispatchEvent( event );
}

/**
 * Trigger any actions needed when filtered content is activated
 *
 * @param {HTMLElement} contentBlock - The filtered content element
 */
function triggerContentActivation( contentBlock ) {
	// If the content contains any Swiper instances, reinitialize them
	if ( typeof Swiper !== 'undefined' ) {
		const swipers = contentBlock.querySelectorAll( '.swiper' );
		swipers.forEach( ( swiperEl ) => {
			// Trigger Swiper update if it exists
			if ( swiperEl.swiper ) {
				swiperEl.swiper.update();
			}
		} );
	}

	// Trigger lazy loading for images if needed
	const lazyImages = contentBlock.querySelectorAll( 'img[loading="lazy"]' );
	lazyImages.forEach( ( img ) => {
		if ( img.dataset.src ) {
			img.src = img.dataset.src;
			img.removeAttribute( 'data-src' );
		}
	} );
}

/**
 * Initialize on DOM ready
 */
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initFilteredContent );
} else {
	initFilteredContent();
}

/**
 * Re-initialize if filters are added dynamically (e.g., via AJAX)
 * You can trigger this with: document.dispatchEvent(new Event('filterReload'))
 */
document.addEventListener( 'filterReload', initFilteredContent );

